import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ExampleCreativeCard } from "./ExampleCreativeCard.js";

// Matches the grid's Tailwind breakpoint: `grid-cols-2 sm:grid-cols-3`.
// `sm` is a viewport media query (640px), so matching on window width mirrors
// exactly what the static grid would render.
const SM_BREAKPOINT_PX = 640;
const COLUMNS_BASE = 2;
const COLUMNS_SM = 3;

// Row height is refined per-row by measureElement; this estimate only seeds the
// initial scrollbar size. A card is an aspect-[4/5] thumbnail + caption + a
// 3-metric strip, plus the gap-4 (16px) row spacing carried as bottom padding.
const ESTIMATED_ROW_PX = 320;
const ROW_OVERSCAN = 3;

/** Normalized view model rendered by {@link ExampleCreativeCard}. */
export interface ExampleCardVm {
  key: string;
  thumbnailUrl: string;
  /** Full-quality media opened in the lightbox. Empty makes the card non-interactive. */
  src?: string;
  type: "video" | "image";
  title: string;
  subtitle?: string;
  /** When set, the subtitle renders as an external link to this URL. */
  subtitleHref?: string;
  /** Extra context shown in the lightbox footer (dates, ad count, etc.). */
  meta?: string;
  /** Optional accent colour tying the card back to its chart point. */
  accentColor?: string;
  metrics: { label: string; value: string }[];
}

interface ExampleCreativeGridProps {
  cards: ExampleCardVm[];
  emptyLabel: string;
  /**
   * The drawer's scroll container. When provided the grid virtualizes against
   * it; when absent (Storybook, isolated tests) it falls back to a plain grid so
   * off-context usage stays unchanged.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

/**
 * Tracks the active column count, mirroring the grid's `sm:grid-cols-3`
 * breakpoint. Returns 2 below 640px, 3 at or above.
 */
function useColumnCount(): number {
  const [cols, setCols] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= SM_BREAKPOINT_PX ? COLUMNS_SM : COLUMNS_BASE,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(min-width: ${SM_BREAKPOINT_PX}px)`);
    const update = () => setCols(mq.matches ? COLUMNS_SM : COLUMNS_BASE);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return cols;
}

function PlainGrid({ cards }: { cards: ExampleCardVm[] }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {cards.map((card) => (
        <ExampleCreativeCard key={card.key} card={card} />
      ))}
    </div>
  );
}

/**
 * Row-virtualized creatives grid mirroring the opportunities drawer. Only rows
 * within the viewport (plus a small overscan) are mounted, so the number of live
 * <img>/<video> elements — and the decoded bitmaps the browser retains — stays
 * bounded regardless of how many pages have been loaded.
 */
export function ExampleCreativeGrid({ cards, emptyLabel, scrollContainerRef }: ExampleCreativeGridProps) {
  const cols = useColumnCount();
  const gridRef = useRef<HTMLDivElement>(null);
  const rowCount = Math.ceil(cards.length / cols);

  // The grid sits inside the drawer scroll container, so the virtualizer needs
  // the grid's offset within that scroller to map scroll position onto rows.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    const scroller = scrollContainerRef?.current;
    if (!grid || !scroller) return;

    const measure = () => {
      const offset =
        grid.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      setScrollMargin(offset);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [scrollContainerRef, cards.length, cols]);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => ESTIMATED_ROW_PX,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  });

  // When the grid mounts in the same commit as a freshly-created scroll
  // container, the virtualizer can latch a 0×0 viewport before layout resolves
  // and render no rows. Re-measure once after layout settles. Gate on the ref
  // object (not `.current`): only the virtualized path receives one, so the
  // plain-grid fallback schedules nothing.
  useLayoutEffect(() => {
    if (!scrollContainerRef) return;
    const id = requestAnimationFrame(() => virtualizer.measure());
    return () => cancelAnimationFrame(id);
  }, [virtualizer, scrollContainerRef]);

  if (cards.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">{emptyLabel}</p>;
  }

  // No scroll container in context (stories / isolated tests): render everything.
  if (!scrollContainerRef) {
    return <PlainGrid cards={cards} />;
  }

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div ref={gridRef} style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
      {virtualRows.map((virtualRow) => {
        const start = virtualRow.index * cols;
        const rowCards = cards.slice(start, start + cols);
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            {/* pb-4 carries the gap-4 row spacing into the measured row height */}
            <div className="grid grid-cols-2 gap-4 pb-4 sm:grid-cols-3">
              {rowCards.map((card) => (
                <ExampleCreativeCard key={card.key} card={card} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
